// View-model helpers for the candidate portal: everything here is derived from
// the /candidate/me response (the wizard's `form` shape), nothing new is stored.

import { BLOOD_TYPES, COUNTRIES, GENDERS, ID_TYPES, SECURITY_DOCS } from "../../constants";

// The wizard's 4 steps, as the portal presents them. A section is complete once
// application_step (completed steps, see docs/login-feature-context.md) is past it.
// The last one has no card or tab: it opens in a modal once Residencia is done (see DeclarationModal).
export const SECTIONS = [
  { step: 0, tab: "personal", title: "Datos personales" },
  { step: 1, tab: "documents", title: "Documentación" },
  { step: 2, tab: "residence", title: "Residencia" },
  { step: 3, tab: "declaration", title: "Declaración", modal: true },
];

// The steps that have a card (and a tab) in the portal.
export const TAB_SECTIONS = SECTIONS.filter((section) => !section.modal);

// Same threshold as the wizard's expiryInfo(): within 30 days reads as "por vencer".
const EXPIRING_SOON_DAYS = 30;

const MONTHS = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "setiembre",
  "octubre",
  "noviembre",
  "diciembre",
];
const MONTHS_SHORT = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const WEEKDAYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

export function applicationStep(candidate) {
  return (candidate && candidate.application_step) || 0;
}

// "done" once application_step is past it, "current" if it's the next one to complete, "locked" otherwise
// (sections are completed in order: Datos personales carries the cédula every later save needs).
export function sectionState(step, candidate) {
  const completed = applicationStep(candidate);
  if (step < completed) return "done";
  if (step === completed) return "current";
  return "locked";
}

// ---- dates ----

// "YYYY-MM-DD" as a local date (new Date("YYYY-MM-DD") would be UTC midnight, i.e. the day before in Costa Rica).
export function parseDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return isNaN(date) ? null : date;
}

// "22 sep 2026"
export function formatDate(value) {
  const date = parseDate(value);
  if (!date) return "";
  return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]} ${date.getFullYear()}`;
}

// "Martes 30 de setiembre"
export function formatLongDate(date) {
  return `${WEEKDAYS[date.getDay()]} ${date.getDate()} de ${MONTHS[date.getMonth()]}`;
}

export function monthShort(value) {
  const date = parseDate(value);
  return date ? MONTHS_SHORT[date.getMonth()] : "";
}

export function dayOfMonth(value) {
  const date = parseDate(value);
  return date ? date.getDate() : "";
}

export function yearOf(value) {
  const date = parseDate(value);
  return date ? date.getFullYear() : "";
}

// Whole days from today to the date (negative once past), or null without a valid date.
export function daysUntil(value) {
  const date = parseDate(value);
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((date - today) / 86400000);
}

// Badge for an expiration date: { tone, label, detail }, or null without one.
export function expiryStatus(value) {
  const days = daysUntil(value);
  if (days === null) return null;
  const date = formatDate(value);
  if (days < 0) return { tone: "danger", label: "Vencido", detail: `Venció el ${date}`, days };
  if (days === 0) return { tone: "danger", label: "Vence hoy", detail: `Vence hoy, ${date}`, days };
  if (days <= EXPIRING_SOON_DAYS) {
    return { tone: "warning", label: `Vence en ${days} ${days === 1 ? "día" : "días"}`, detail: `Vence el ${date}`, days };
  }
  return { tone: "success", label: "Vigente", detail: `Vence el ${date}`, days };
}

// Whole years since the date (e.g. age from birthdate), or null without a valid date.
export function yearsSince(value) {
  const date = parseDate(value);
  if (!date) return null;
  const today = new Date();
  let years = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) years -= 1;
  return years;
}

// ---- person ----

// Names found in the civil registry arrive in capitals ("ALEJANDRO VASQUEZ"); shown as "Alejandro Vasquez".
// Anything typed with mixed case is left as the applicant wrote it.
export function displayName(value) {
  const text = (value || "").trim();
  if (!text || text !== text.toUpperCase()) return text;
  return text.toLowerCase().replace(/(^|[\s-])(\S)/g, (match, sep, letter) => sep + letter.toUpperCase());
}

export function fullName(candidate) {
  return displayName([candidate.first_name, candidate.last_name, candidate.sur_name].filter(Boolean).join(" "));
}

export function firstName(candidate) {
  return displayName((candidate.first_name || "").trim().split(/\s+/)[0]);
}

export function initials(candidate) {
  return [candidate.first_name, candidate.last_name]
    .filter(Boolean)
    .map((part) => part.trim().charAt(0))
    .join("")
    .toUpperCase();
}

function optionText(options, value) {
  if (value === null || value === undefined || value === "") return "";
  const option = options.find((opt) => String(opt.value) === String(value));
  return option ? option.text : "";
}

export function idTypeText(candidate) {
  return optionText(ID_TYPES, candidate.identification_type_id);
}

export function bloodTypeText(candidate) {
  return optionText(BLOOD_TYPES, candidate.blood_type_id);
}

export function countryText(candidate) {
  return optionText(COUNTRIES, candidate.country_id);
}

export function genderText(candidate) {
  return optionText(GENDERS, candidate.gender_id);
}

// The wizard stores its Sí/No radios as "true"/"false".
export function yesNoText(value) {
  if (value === "true" || value === true) return "Sí";
  if (value === "false" || value === false) return "No";
  return "";
}

// ---- documents ----

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "heic", "heif"];

// Stored keys keep the uploaded file's extension (rrhh/<stamp>-<token>.<ext>). Only images can be
// shown in the portal: files are stored without a content type, which an <img> doesn't need but
// anything else does (a PDF link just downloads).
export function isImageFile(prefix) {
  const match = /\.([a-z0-9]+)$/i.exec(prefix || "");
  return !!match && IMAGE_EXTENSIONS.includes(match[1].toLowerCase());
}

// "Sí" / "No" / "" for a security doc — portación is stored as "true"/"false", the rest as "Sí"/"No".
export function securityAnswer(candidate, doc) {
  if (doc.key === "up_to_date_portation") return yesNoText(candidate.up_to_date_portation);
  return candidate[doc.key] || "";
}

function documentStatus(row) {
  if (row.missing) return { tone: "danger", label: "Pendiente" };
  if (row.answer === "No") return { tone: "neutral", label: "No" };
  if (row.answer === "") return { tone: "neutral", label: "Sin responder" };
  if (row.isExpiry && row.date) return expiryStatus(row.date) || { tone: "success", label: "Cargado" };
  return { tone: "success", label: "Cargado" };
}

// Every document the Documentación step collects, one row each, grouped for display:
// { key, group, label, files: [{ label, prefix }], date, dateLabel, isExpiry, answer, missing, status }.
export function documentRows(candidate) {
  const rows = [
    {
      key: "cedula",
      group: "identity",
      label: "Cédula / identificación",
      files: [
        { label: "Frente", prefix: candidate.cedula_front_url },
        { label: "Reverso", prefix: candidate.cedula_back_url },
      ].filter((file) => file.prefix),
      date: candidate.cedula_expiration_date,
      dateLabel: "Vence",
      isExpiry: true,
      missing: !candidate.cedula_front_url || !candidate.cedula_back_url,
    },
    {
      key: "hoja_delincuencia",
      group: "identity",
      label: "Hoja de delincuencia",
      files: candidate.hoja_delincuencia_url ? [{ label: "Documento", prefix: candidate.hoja_delincuencia_url }] : [],
      date: candidate.hoja_delincuencia_expiration_date,
      dateLabel: "Vence",
      isExpiry: true,
      missing: !candidate.hoja_delincuencia_url,
    },
  ];

  (candidate.driving_licenses || []).forEach((license, idx) => {
    rows.push({
      key: `license-${idx}`,
      group: "licenses",
      label: `Licencia de conducir ${license.license_id}`,
      files: license.file_url ? [{ label: "Documento", prefix: license.file_url }] : [],
      date: license.expiration_date,
      dateLabel: "Vence",
      isExpiry: true,
      missing: !license.file_url,
    });
  });

  SECURITY_DOCS.forEach((doc) => {
    const answer = securityAnswer(candidate, doc);
    const has = answer === "Sí";
    rows.push({
      key: doc.key,
      group: "security",
      label: doc.label,
      shortLabel: doc.shortLabel,
      files: has && candidate[doc.photoKey] ? [{ label: "Documento", prefix: candidate[doc.photoKey] }] : [],
      date: has ? candidate[doc.dateKey] : "",
      dateLabel: doc.isExpiry ? "Vence" : "Realizado",
      isExpiry: doc.isExpiry,
      answer,
      missing: has && !candidate[doc.photoKey],
    });
  });

  (candidate.reference_documents || []).forEach((reference, idx) => {
    rows.push({
      key: `reference-${idx}`,
      group: "references",
      label: `Referencia ${idx + 1}`,
      files: [{ label: "Documento", prefix: reference.file_url }],
      date: "",
      dateLabel: "",
      isExpiry: false,
      missing: false,
    });
  });

  return rows.map((row) => ({ ...row, status: documentStatus(row) }));
}

// The line under a document's name: its answer, what's missing, or its date.
export function documentMeta(row) {
  if (row.answer === "No") return "Indicó que no cuenta con este requisito.";
  if (row.answer === "") return "Sin responder.";
  if (row.missing) return "Falta adjuntar el documento.";
  if (row.date) return `${row.dateLabel} el ${formatDate(row.date)}`;
  return row.isExpiry ? "Sin fecha de vencimiento registrada." : "Documento adjunto.";
}

// Uploaded documents with an expiration date, soonest first.
export function expiringDocuments(rows) {
  return rows
    .filter((row) => row.isExpiry && row.files.length && daysUntil(row.date) !== null)
    .sort((a, b) => daysUntil(a.date) - daysUntil(b.date));
}

// Expired or about-to-expire documents — the portal's "acciones pendientes".
export function documentAlerts(rows) {
  return expiringDocuments(rows).filter((row) => row.status.tone === "danger" || row.status.tone === "warning");
}

// The document the "próxima fecha" card is about: the one expired the longest if any is, otherwise
// the next expiration still ahead (today included).
export function nextExpiration(rows) {
  const dated = expiringDocuments(rows);
  return dated.find((row) => daysUntil(row.date) < 0) || dated.find((row) => daysUntil(row.date) >= 0) || null;
}
